import { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import Quill from 'quill';
import 'react-quill/dist/quill.snow.css';

// A tárolt HTML-ben (pl. migrációval beszúrt sablonokban) lehet sortörés a blokk-elemek között.
// Ha ezt változatlanul a szerkesztő gyökerébe írjuk, a Quill a listákat ("<ul>, sortörés, <li>")
// elveti, és minden blokk közé üres bekezdést tesz. Ezért betöltés előtt eltávolítjuk a
// blokk-elemek közötti sortöréseket.
const BLOCK_TAGS = 'p|h[1-6]|ul|ol|li|blockquote|div|pre';
const BREAK_BEFORE_BLOCK = new RegExp('>\\s*\\n\\s*(?=</?(?:' + BLOCK_TAGS + ')\\b)', 'gi');
const BREAK_AFTER_CLOSING_BLOCK = new RegExp('(</(?:' + BLOCK_TAGS + ')>)\\s*\\n\\s*', 'gi');
export const normalizeEditorHtml = (html) => String(html || '')
  .replace(BREAK_BEFORE_BLOCK, '>')
  .replace(BREAK_AFTER_CLOSING_BLOCK, '$1');

// Uses quill v1 directly via a DOM ref, bypassing react-quill's findDOMNode
// (removed in React 19).
//
// DOM structure after Quill init (same as react-quill produced):
//   parent (MUI Box)
//     ├── .ql-toolbar   ← Quill inserts this before the container
//     └── wrapperRef    ← becomes .ql-container
//           └── .ql-editor
//
// Cleanup stores a ref to the toolbar so it can be removed even when the
// container div is already detached from the DOM (e.g. after a key-change remount).
const QuillEditor = forwardRef(({ value, onChange, modules, placeholder }, ref) => {
  const wrapperRef = useRef(null);
  const quillRef = useRef(null);
  const toolbarRef = useRef(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useImperativeHandle(ref, () => ({
    clear: () => {
      const quill = quillRef.current;
      if (quill) quill.setText('');
    },
    // Szöveg beszúrása a kurzor helyére (vagy a végére, ha nincs fókusz) — pl. sablonváltozók
    insertText: (text) => {
      const quill = quillRef.current;
      if (!quill) return;
      const range = quill.getSelection(true);
      const index = range ? range.index : quill.getLength() - 1;
      quill.insertText(index, text, 'user');
      quill.setSelection(index + text.length, 0);
    },
  }));

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || quillRef.current) return;

    const quill = new Quill(el, {
      theme: 'snow',
      modules: modules || {},
      placeholder: placeholder || '',
    });
    quillRef.current = quill;
    toolbarRef.current = el.previousElementSibling;

    if (value) quill.root.innerHTML = normalizeEditorHtml(value);

    quill.on('text-change', () => {
      onChangeRef.current(quill.root.innerHTML);
    });

    return () => {
      quillRef.current = null;
      if (toolbarRef.current?.parentNode) toolbarRef.current.remove();
      toolbarRef.current = null;
      el.className = '';
      el.innerHTML = '';
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <div ref={wrapperRef} />;
});

export default QuillEditor;
